import AbortController from 'abort-controller';
import * as DateFns from 'date-fns';
import { Dispatch } from 'redux';

import { cleanControllerSet, getTeamProps, isNCAA, isTA, paramBuilder } from '../../helpers/helpers';
import { routeToSpecificScope } from '../../helpers/helpersts';
import { addLoadingFlag, changeMessageLoading, subLoadingFlag } from './loading';
import { requestData } from '../../config/services/datasets';
import { SERVER } from '../../config/server.config';
import { CONTROLLER_SET, LEAGUES, MESSAGES, PAST } from '../../config/constants';
import {
  DataPlayer,
  DataPlayerEventsGames, DataPlayerEventsPractices,
  DataPlayersAction,
  DataPlayersState,
  MetaDataPlayerSearched,
  UserType
} from '../types';
import { replaceHighlightsDataEvent, toDataEvents, updateMetaDataEvent, replaceZonesThresholdsDataEvent } from './dataEvents';
import { RootState } from '../reducers';
import { AppThunk } from '../../config/commonTypes';

export const replaceDataPlayers = (players: DataPlayersState) => {
  return {
    type: DataPlayersAction.REPLACE_DATA_PLAYERS,
    players
  };
};

export const updateDataPlayer = (player: DataPlayer) => {
  return {
    type: DataPlayersAction.UPDATE_DATA_PLAYER,
    player
  };
};

export const buildPlayerData = (player: MetaDataPlayerSearched) => {
  const meta = {
    name: player.name,
    playerId: player.nid,
    position: player.position,
    positionGroup: player.positionGroup,
    status: player.status,
    sbeid: player.sbeid,
    teamId: player.teamId,
    jersey: player.jersey,
    eid: player.eid
  };
  return {
    meta: meta,
    metrics: player.metrics || {},
    events: {
      games: [],
      practices: []
    }
  };
};

export const getDataPlayersByTeamId = (teamId: string, type?: string): AppThunk => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const path = getState().router.location.pathname;
    const scope = routeToSpecificScope(path);
    dispatch(addLoadingFlag(scope));
    const params = getState().filters[scope] ? JSON.parse(JSON.stringify(getState().filters[scope])) : {};
    params.teamId = teamId;
    if (type === 'Game') {
      params.isGame = 'yes';
    } else if (type === 'Practice') {
      params.isPractice = 'yes';
    }
    params.positions= [];
    const queryParam = paramBuilder(params);
    cleanControllerSet(CONTROLLER_SET);
    const controller = new AbortController();
    CONTROLLER_SET.add(controller);
    const results = await requestData(SERVER.PLAYERS(teamId) + (queryParam ? '?' + queryParam : queryParam), { signal: controller.signal as AbortSignal});
    CONTROLLER_SET.delete(controller);
    if (results) {
      const players: DataPlayersState = {};
      results.forEach((player: MetaDataPlayerSearched) => {
        players[player.nid] = buildPlayerData(player);
      });
      if (Object.keys(players).length === 0) {
        dispatch(changeMessageLoading(scope, MESSAGES.EMPTY));
      }
      dispatch(replaceDataPlayers(players));
    } else if (!controller.signal.aborted) {
      dispatch(changeMessageLoading(scope, MESSAGES.EMPTY));
    }
    dispatch(subLoadingFlag(scope));
  };
};

export const getDataPlayers = (): AppThunk => {
  return async (dispatch, getState) => {
    const path = getState().router.location.pathname;
    const scope = routeToSpecificScope(path);
    const params = getState().filters[scope] ? JSON.parse(JSON.stringify(getState().filters[scope])) : {};
    const teamId = params.teamId ?? (getState().user as UserType).teamId;
    dispatch(getDataPlayersByTeamId(teamId));
  };
};

export const loadDataPlayersOfLocalStorage = () => {
  return (dispatch: Dispatch) => {
    const reducer = 'dataPlayers';
    const recoveredStore = JSON.parse(localStorage.getItem('store') || '{}');
    if (recoveredStore[reducer]) {
      dispatch(replaceDataPlayers(recoveredStore[reducer]));
    }
  };
};

// Practices of player
export const replaceDataPlayersPlayerPractices = (playerId: string, practices: DataPlayerEventsPractices) => {
  return {
    type: DataPlayersAction.REPLACE_DATA_PLAYERS_PLAYER_PRACTICES,
    playerId,
    practices
  };
};

// Games of Player
export const replaceDataPlayersPlayerGames = (playerId: string, games: DataPlayerEventsGames) => {
  return {
    type: DataPlayersAction.REPLACE_DATA_PLAYERS_PLAYER_GAMES,
    playerId,
    games
  };
};

export const getDataPlayersPlayerPractices = (playerId: string): AppThunk => {
  return async (dispatch, getState) => {
    const teamId = getState().dataPlayers[playerId].meta.teamId;
    const league = (getTeamProps(teamId)||{league: ''}).league;
    const path = getState().router.location.pathname;
    const scopeForParams = routeToSpecificScope(path);
    const params = getState().filters[scopeForParams] ? JSON.parse(JSON.stringify(getState().filters[scopeForParams])) : {};
    const scope = routeToSpecificScope(path);
    dispatch(addLoadingFlag(scope));
    params.teamId = teamId;
    const user = getState().user;
    if(isTA(user) || isNCAA(user)){
      params.teamOfInterest = (user as UserType).teamId;
    }else{
      params.teamOfInterest = teamId;
    }
    const results = await requestData(SERVER.PLAYER_METRICS(playerId) + paramBuilder(params));
    if (results && results.length && results.length > 0) {
      if (results.length === 0) {
        dispatch(changeMessageLoading(scope, MESSAGES.EMPTY));
      }
      const practices: DataPlayerEventsPractices = [];
      const games: DataPlayerEventsGames = [];
      // TODO: correct the type
      results.forEach((event: any) => {
        if (event[Object.keys(event)[0]].isGame) {
          games.push({
            ...event[Object.keys(event)[0]],
            homeTeamId: teamId,
            gameId: Object.keys(event)[0]
          });
        } else {
          practices.push({
            ...event[Object.keys(event)[0]],
            homeTeamId: event[Object.keys(event)[0]].teamId || teamId,
            gameId: Object.keys(event)[0],
            name: event[Object.keys(event)[0]].practiceName
          });

        }
      });
      // BEGIN DataEvents
      // TODO: correct the type
      const practicesDataEvents: any[] = practices.map((practice) => toDataEvents({ ...practice, status: PAST }));
      practicesDataEvents.forEach((practice) => {
        dispatch(updateMetaDataEvent(practice.meta.gameId, practice.meta));
        dispatch(replaceHighlightsDataEvent(practice.meta.gameId, practice.highlights));
        dispatch(replaceZonesThresholdsDataEvent(practice.meta.gameId, practice.zones));
      });
      // END Data Events
      dispatch(replaceDataPlayersPlayerPractices(playerId, practices));
      let processedGames = [];
      //get unique teamId values for prev seasons
      const flags = [], diffTeamIds = [], l = games.length;
      let i;
      for (i = 0; i < l; i++) {
        if (flags[games[i].teamId]) {
          continue;
        }
        flags[games[i].teamId] = true;
        diffTeamIds.push(games[i].teamId);
      }

      let additionalScores = [];
      let scores: DataPlayerEventsGames = [];
      for (i = 0; i < diffTeamIds.length; i++) {
        params.teamId = diffTeamIds[i];
        additionalScores = await requestData(SERVER.GAMES_SCORE(playerId) + paramBuilder(params));
        league === LEAGUES.NCAA ? scores = [...games] : scores = [...scores, ...additionalScores];
      }

      if (scores) {
        processedGames = scores.map((game) => {
          const gameMetrics = games.find(metric => {
            return metric.gameId === game.gameId;
          });
          return {
            ...game,
            metrics: gameMetrics ? gameMetrics : null,
            homeTeamId: teamId,
            startDate: new Date(league === LEAGUES.NCAA ? game.startTime :game.ts),
            end_date: gameMetrics ? new Date(gameMetrics.endTime) : null
          };
        });

        processedGames.filter(game => {
          return game.metrics !== null || DateFns.isFuture(game.startDate);
        });
      }

      dispatch(replaceDataPlayersPlayerGames(playerId, processedGames));

    } else {
      dispatch(changeMessageLoading(scope, MESSAGES.EMPTY));
    }
    dispatch(subLoadingFlag(scope));
  };
};
